import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { formatDuration, type ScoredCandidate } from '@srcbattle/shared';
import { api, ApiRequestError, type EntryView, type LibrarySong } from '../api.ts';
import { Banner, Button, Card, Field } from './ui.tsx';
import { CandidatePicker } from './CandidatePicker.tsx';
import { ClipPicker } from './ClipPicker.tsx';

interface PendingChoice {
  track: { trackId: string; title: string; artist: string; durationMs: number; albumArtUrl: string | null };
  candidates: ScoredCandidate[];
}


export function EntrySubmit({
  lobbyId,
  slots,
  spotifyAvailable,
  onChanged,
}: {
  lobbyId: string;
  slots: number;
  spotifyAvailable: boolean;
  onChanged: () => void;
}) {
  const [entries, setEntries] = useState<EntryView[] | null>(null);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingChoice | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const [library, setLibrary] = useState<LibrarySong[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryGroup, setLibraryGroup] = useState('all');
  const [selected, setSelected] = useState<string[]>([]);
  const [entriesExpanded, setEntriesExpanded] = useState(true);

  const load = useCallback(async () => {
    try {
      const { entries: mine } = await api.myEntries(lobbyId);
      setEntries(mine);
    } catch {
      setEntries([]);
    }
  }, [lobbyId]);

  useEffect(() => {
    void load();
    void api.library().then((result) => setLibrary(result.songs)).catch(() => {});
  }, [load]);

  const saveClip = async (entryId: string, startMs: number, durationMs: number) => {
    try {
      const { entry } = await api.setClipStart(lobbyId, entryId, startMs, durationMs);
      // Replace in place rather than reloading the list: a reload would reset
      // every other slider on the page to whatever the server last saw.
      setEntries((current) =>
        current ? current.map((e) => (e.id === entryId ? entry : e)) : current,
      );
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save that.');
    }
  };

  const remaining = slots - (entries?.length ?? 0);
  const tooManySelected = selected.length > remaining;
  const libraryGroups = Array.from(new Set(library.map((song) => song.groupName).filter((name): name is string => Boolean(name)))).sort((a, b) => a.localeCompare(b));
  const normalizedLibraryQuery = libraryQuery.trim().toLocaleLowerCase();
  const visibleLibrary = library.filter((song) =>
    (libraryGroup === 'all' || (libraryGroup === 'ungrouped' ? !song.groupName : song.groupName === libraryGroup)) &&
    (!normalizedLibraryQuery || `${song.title} ${song.artist ?? ''}`.toLocaleLowerCase().includes(normalizedLibraryQuery)),
  );

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;

    setBusy(true);
    setError(null);
    try {
      const result = await api.submitEntry(lobbyId, trimmed);
      if (result.status === 'added') {
        setUrl('');
        await load();
        onChanged();
      } else {
        // Spotify link: hold the URL box empty but keep the picker open.
        setUrl('');
        setPending({ track: result.track, candidates: result.candidates });
      }
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not add that.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm(videoId: string) {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await api.confirmEntry(lobbyId, pending.track.trackId, videoId);
      setPending(null);
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not add that.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(entryId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.removeEntry(lobbyId, entryId);
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not remove that.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Adds one or more saved songs as entries.
   *
   * Sequential rather than parallel: the server counts entries against the
   * member's slots on every submission, and firing four at once races that
   * check. One at a time also means a song the server refuses — a duplicate,
   * or a Spotify track with no YouTube match — stops only itself, and the rest
   * of the selection still lands.
   */
  async function addFromLibrary(songs: LibrarySong[]) {
    if (songs.length === 0) return;
    setBusy(true);
    setError(null);

    const failures: string[] = [];
    let added = 0;
    for (const song of songs) {
      try {
        const result = await api.submitLibrarySong(song.id, lobbyId);
        if (result.status === 'added') added += 1;
        else failures.push(`${song.title} needs a YouTube link`);
      } catch (caught) {
        failures.push(`${song.title}: ${caught instanceof ApiRequestError ? caught.message : 'could not be added'}`);
      }
    }

    setSelected((current) => current.filter((id) => songs.every((song) => song.id !== id)));
    if (added > 0) {
      await load();
      onChanged();
    }
    if (failures.length > 0) setError(failures.join(' · '));
    else setShowLibrary(false);
    setBusy(false);
  }

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-medium">Your entries</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-muted">
            {entries?.length ?? 0} of {slots}
          </span>
          <Button
            variant="ghost"
            className="px-2 py-1.5"
            aria-expanded={entriesExpanded}
            aria-controls="my-entry-list"
            onClick={() => setEntriesExpanded((expanded) => !expanded)}
          >
            {entriesExpanded ? 'Hide entries' : 'Show entries'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-3">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      {pending ? (
        <CandidatePicker
          track={pending.track}
          candidates={pending.candidates}
          busy={busy}
          onConfirm={confirm}
          onCancel={() => setPending(null)}
        />
      ) : (
        <>
          {entriesExpanded && entries && entries.length > 0 && (
            <ul id="my-entry-list" className="mb-4 flex flex-col gap-2">
              {entries.map((entry) => (
                <li
                  key={entry.id}
                  className="rounded-lg border border-border bg-surface-raised p-2"
                >
                  <div className="flex items-center gap-3">
                  {entry.thumbnailUrl ? (
                    <img
                      src={entry.thumbnailUrl}
                      alt=""
                      className="h-10 w-16 shrink-0 rounded object-cover"
                    />
                  ) : (
                    <span className="h-10 w-16 shrink-0 rounded bg-surface" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{entry.title}</p>
                    <p className="truncate text-xs text-text-faint">
                      {entry.durationMs > 0 ? formatDuration(entry.durationMs) : 'length unknown'}
                      {entry.clipStartMs > 0 && ` · starts at ${formatDuration(entry.clipStartMs)}`}
                    </p>
                  </div>
                  {entry.canChooseClip && (
                    <Button
                      variant="ghost"
                      onClick={() => setPicking((open) => (open === entry.id ? null : entry.id))}
                      disabled={busy}
                    >
                      {picking === entry.id
                        ? 'Done'
                        : entry.clipStartMs > 0
                          ? 'Change start'
                          : 'Pick start'}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => void remove(entry.id)}
                    disabled={busy}
                    aria-label={`Remove ${entry.title}`}
                  >
                    Remove
                  </Button>
                  </div>
                  {entry.canChooseClip && picking === entry.id && (
                    <ClipPicker
                      entry={entry}
                      onSave={(startMs, durationMs) => saveClip(entry.id, startMs, durationMs)}
                      onClose={() => setPicking(null)}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}

          {remaining > 0 ? (
            <div className="flex flex-col gap-3">
            {library.length > 0 && <div><Button variant="secondary" onClick={() => setShowLibrary((shown) => !shown)}>{showLibrary ? 'Hide library' : 'Choose from song library'}</Button></div>}
            {showLibrary && (
              <div className="rounded-lg border border-border p-2">
                <div className="mb-2 grid gap-2 sm:grid-cols-[1fr_auto]">
                  <input
                    type="search"
                    aria-label="Search song library"
                    value={libraryQuery}
                    onChange={(event) => setLibraryQuery(event.target.value)}
                    placeholder="Search title or artist"
                    className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text"
                  />
                  <select
                    aria-label="Library group"
                    value={libraryGroup}
                    onChange={(event) => setLibraryGroup(event.target.value)}
                    className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text"
                  >
                    <option value="all">All songs</option>
                    <option value="ungrouped">Ungrouped</option>
                    {libraryGroups.map((name) => <option key={name} value={name}>{name}</option>)}
                  </select>
                </div>

                <ul className="max-h-64 divide-y divide-border overflow-y-auto">
                  {visibleLibrary.map((song) => {
                    const checked = selected.includes(song.id);
                    return (
                      <li key={song.id} className="flex items-center gap-2 px-1 py-1.5">
                        {/* The whole row is the label, so a checkbox on a phone
                            is a comfortable target rather than a 16px square. */}
                        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                          <input
                            type="checkbox"
                            className="size-4 shrink-0 accent-[var(--accent)]"
                            checked={checked}
                            disabled={busy}
                            onChange={(event) =>
                              setSelected((current) =>
                                event.target.checked
                                  ? [...current, song.id]
                                  : current.filter((id) => id !== song.id),
                              )
                            }
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm">{song.title}</span>
                            {song.groupName && (
                              <span className="block truncate text-[11px] text-text-faint">{song.groupName}</span>
                            )}
                          </span>
                        </label>
                        <Button variant="ghost" disabled={busy} onClick={() => void addFromLibrary([song])}>
                          Add
                        </Button>
                      </li>
                    );
                  })}
                  {visibleLibrary.length === 0 && <li className="p-3 text-sm text-text-muted">No songs match.</li>}
                </ul>

                {selected.length > 0 && (
                  <div className="mt-2 border-t border-border pt-2">
                    {/* Refused before it is attempted rather than after: adding
                        five of seven and erroring on the rest would leave the
                        member guessing which two did not make it. */}
                    {tooManySelected && (
                      <p role="alert" className="mb-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-text">
                        {selected.length} selected, but you only have {remaining} slot{remaining === 1 ? '' : 's'} left.
                        Uncheck {selected.length - remaining} of them, or remove an entry above.
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        disabled={busy || tooManySelected}
                        onClick={() => void addFromLibrary(library.filter((song) => selected.includes(song.id)))}
                      >
                        Add {selected.length} selected
                      </Button>
                      <Button variant="ghost" disabled={busy} onClick={() => setSelected([])}>
                        Clear
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={onSubmit} className="flex flex-col gap-3">
              <Field
                label={`Add an entry (${remaining} to go)`}
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                hint={
                  spotifyAvailable
                    ? 'YouTube or Spotify link.'
                    : 'YouTube link.'
                }
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                inputMode="url"
                enterKeyHint="done"
              />
              <Button type="submit" loading={busy} disabled={!url.trim()}>
                Add
              </Button>
            </form>
            </div>
          ) : (
            <Banner tone="success">
              All {slots} in. Remove one if you want to swap it out.
            </Banner>
          )}
        </>
      )}
    </Card>
  );
}
